import { defineStore } from "pinia"
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { Group } from "./ResearchDataModel"
import { DateTime } from "luxon"
import { useResearchDataApi } from "./ResearchDataApi"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { plainToStatistics } from "./WorkspaceStore"

export const useGroupStore = defineStore("groupStore", () => {
    const asyncGroups = new Map<string, AsyncData<Group>>()
    const asyncWorkspaceKeys = new Map<string, AsyncData<VersionedKeyList>>()

    const rd = useResearchDataApi()

    function getAsyncGroupList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useGroupStore() // use to get/return reactive store objects
        const loadGroupsAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainGroups = await rd.getPlainByIdsAsync("groups", idsToLoad)
            return processExpandedGroups(plainGroups, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncGroups, ids, "Group", loadMode, loadGroupsAsync)
    }

    function getLoadedGroup(id: string | undefined) {
        return getAsyncGroupList([id]).at(0)?.data
    }

    async function getGroupListAsync(ids: (string | undefined)[], reload = false) {
        const asyncGroups = getAsyncGroupList(ids, reload ? LoadMode.Reload : LoadMode.EnsureLoaded)
        const groups = await Promise.all(asyncGroups.map(a => a.whenLoadCompleted))
        return groups.filter(isDefined)
    }

    async function getGroupDataAsync(id: string) {
        return await rd.getRawAsync(`groups/${id}`, { expand: 'data' })
    }

    async function reprocessAsync(id: string, force = false) {
        await rd.postPlainAsync(`groups/${id}/indexes/update`, undefined, { force })
    }

    async function cleanAsync(id: string) {
        const plainCounts = await rd.postPlainAsync(`groups/${id}/clean`, undefined)
        return plainToStatistics(plainCounts)
    }

    function processExpandedGroups(plainGroups: any[], requestTime: DateTime) {
        return AsyncDataStore.createItemMap(plainGroups, "Group", plainToGroup, g => g.id!)
    }

    return {
        asyncGroups,
        asyncWorkspaceKeys,

        getAsyncGroupList,
        getLoadedGroup,
        getGroupListAsync,
        getGroupDataAsync,
        reprocessAsync,
        cleanAsync,
    }
})

function plainToGroup(p: any) {
    const group = assignExisting(new Group(), p)
    group.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
    group.dataModifiedDate = DateTimeUtil.fromAPI(p.dataModifiedDate)
    group.processingCheckpoint = DateTimeUtil.fromAPI(p.processingCheckpoint)
    return group
}

